import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { trackEvent } from '../../../lib/analytics';
import { useMixpanel } from '../../../lib/mixpanel';
import { agentsApi } from '../api/agentsApi';
import { AGENT_KEYS } from './useAgents';

export const TOOL_ACTION_KEYS = {
  all: [...AGENT_KEYS.all, 'tool-actions'] as const,
  list: (orgId: string, executionId?: string, status?: string) =>
    [...TOOL_ACTION_KEYS.all, orgId, executionId, status] as const,
  availableTools: [...AGENT_KEYS.all, 'available-tools'] as const,
};

export function useToolActions(organizationId: string, executionId?: string, status?: string) {
  return useQuery({
    queryKey: TOOL_ACTION_KEYS.list(organizationId, executionId, status),
    queryFn: () =>
      agentsApi.listToolActions({
        organizationId,
        executionId,
        status,
      }),
    enabled: !!organizationId,
    staleTime: 5 * 60_000,
  });
}

export function useApproveToolAction() {
  const queryClient = useQueryClient();
  const mp = useMixpanel();

  return useMutation({
    mutationFn: (actionId: string) => agentsApi.approveToolAction(actionId),
    onSuccess: () => {
      trackEvent(mp, 'tool_action.approved');
      queryClient.invalidateQueries({
        queryKey: TOOL_ACTION_KEYS.all,
      });
    },
  });
}

export function useRejectToolAction() {
  const queryClient = useQueryClient();
  const mp = useMixpanel();

  return useMutation({
    mutationFn: ({ actionId, reason }: { actionId: string; reason?: string }) =>
      agentsApi.rejectToolAction(actionId, reason),
    onSuccess: () => {
      trackEvent(mp, 'tool_action.rejected');
      queryClient.invalidateQueries({
        queryKey: TOOL_ACTION_KEYS.all,
      });
    },
  });
}

export function useAvailableTools() {
  return useQuery({
    queryKey: TOOL_ACTION_KEYS.availableTools,
    queryFn: () => agentsApi.getAvailableTools(),
    staleTime: 5 * 60 * 1000,
  });
}
